// Every figure and date the interface shows is formatted here, once.
//
// Times arrive as epoch *seconds* because that is how the ingest API takes them
// in, and keeping one representation end to end is what stops the classic
// milliseconds slip that silently lands everything in the year 58666.
const NUMBER = new Intl.NumberFormat("en-US");
const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});
const DATE = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

const NOTHING = "—";
const MINUS = "−"; // a real minus, which lines up with digits; a hyphen does not

export function adena(value: number | null): string {
  return value === null ? NOTHING : NUMBER.format(value);
}

/** What to print in a row's price column.
 *
 *  A package is one lot sold whole, so its rows have no per-item price — the
 *  lot's cost goes in package_total and is repeated on every row of it. Showing
 *  that figure bare would read as the price of this one item, which is exactly
 *  the misreading the null price exists to prevent, so it is marked as a lot
 *  price. A package whose price could not be read has neither figure. */
export function offerPrice(row: {
  mode: string;
  price: number | null;
  package_total: number | null;
}): string {
  if (row.mode !== "package") return adena(row.price);
  return row.package_total === null ? NOTHING : `${NUMBER.format(row.package_total)} / lot`;
}

/** The badges that sit beside an item's name: the enchant level, the element
 *  and the special ability a weapon was listed with, in the order the store
 *  window writes them.
 *
 *  An enchant of 0 is not a level. The client stores null for unenchanted gear
 *  — `split_enchant` only ever returns a level it actually read — so a 0 here
 *  would be a figure no window ever showed, and a "+0" badge on every plain
 *  breastplate is noise on nearly every row of the table.
 *
 *  Empty for the ordinary row, which is nearly every row. */
export function variationParts(row: {
  enchant: number | null;
  element: string | null;
  ability: string | null;
}): string[] {
  const parts: string[] = [];
  if (row.enchant) parts.push(`+${row.enchant}`);
  if (row.element) parts.push(row.element);
  if (row.ability) parts.push(row.ability);
  return parts;
}

export function signed(value: number | null): string {
  if (value === null) return NOTHING;
  if (value === 0) return "0";
  return value > 0 ? `+${NUMBER.format(value)}` : `${MINUS}${NUMBER.format(-value)}`;
}

/** A count and the noun it counts, agreeing. "1 items · 1 scans" is the kind of
 *  slip a reader forgives once and then stops trusting the page over, and every
 *  title note on the site counts something — so the rule lives here once rather
 *  than as a ternary per page.
 *
 *  `many` is for the nouns English does not pluralise by adding s; leave it off
 *  for the ones it does.
 *
 *  The figure goes through NUMBER, not through `toLocaleString()` — please do
 *  not change it back. The two agree in en-US, but a German browser reads the
 *  latter as "12.000" and would print that in a title band sitting directly
 *  above a table of adena()-formatted "12,000"s: one number spelled two ways on
 *  one screen. Every figure in this file is formatted by the same formatter,
 *  which is the point of the file. */
export function plural(count: number, one: string, many: string = `${one}s`): string {
  return `${NUMBER.format(count)} ${count === 1 ? one : many}`;
}

export function when(epochSeconds: number): string {
  return DATE_TIME.format(new Date(epochSeconds * 1000));
}

export function day(epochSeconds: number): string {
  return DATE.format(new Date(epochSeconds * 1000));
}

export function duration(startEpoch: number, endEpoch: number | null): string {
  if (endEpoch === null) return NOTHING;
  const total = Math.max(0, Math.round(endEpoch - startEpoch));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

const MINUTE = 60;
const HOUR = 3600;
const DAY = 86_400;
const MONTH = 30 * DAY;

/** How long ago, for a column you scan rather than read. The exact time goes in
 *  the cell's `title`: a relative age answers "is this current?" at a glance and
 *  an absolute one answers "when exactly?", and one column cannot do both.
 *
 *  `now` is injectable so this is testable without freezing the clock.
 *
 *  Clamped at zero. The scanning machine's clock and the browser's are not the
 *  same clock, so an observation a few seconds in the future is ordinary and
 *  must not print as a negative age. Floored, not rounded, so nothing ever
 *  reads as older than it is. */
export function ago(epochSeconds: number, now: number = Date.now() / 1000): string {
  const delta = Math.max(0, now - epochSeconds);
  if (delta < MINUTE) return "just now";
  if (delta < HOUR) return `${Math.floor(delta / MINUTE)}m`;
  if (delta < DAY) return `${Math.floor(delta / HOUR)}h`;
  if (delta < MONTH) return `${Math.floor(delta / DAY)}d`;
  return `${Math.floor(delta / MONTH)}mo`;
}

/** `ago` in a sentence rather than in a column.
 *
 *  `ago` returns a bare "2h", which is right in a narrow column whose header
 *  says what the number means and wrong in a line of prose. The suffix goes on
 *  here rather than in `ago` itself so the columns keep their terse form — and
 *  it is skipped for "just now", which is already a complete phrase and reads
 *  as gibberish with "ago" stuck on the end. */
export function aged(epochSeconds: number, now: number = Date.now() / 1000): string {
  const age = ago(epochSeconds, now);
  return age === "just now" ? age : `${age} ago`;
}
